import { useParams } from "react-router";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { accountsAtom } from "../../stores/galaxy";
import { sharedGalaxiesAtom, sharedGalaxiesLoadedAtom, refreshSharedGalaxiesAtom } from "../../stores/shared";

// useIsSharedGuest reports whether the CURRENT galaxy (URL :account) is one shared
// read-only WITH the viewer — i.e. they hold a claim but are NOT a member
// (docs/design/shared-galaxies.md). A guest may READ the observation surface but
// never write, so the chrome uses this to hide owner/write affordances (observe
// button, settings, member management, ...) that would only 403.
//
// The shared list is lazy; this ensures it's loaded so a deep-link / reload
// resolves the guest flag without needing the switcher to have been opened.
export function useIsSharedGuest(): boolean {
  const { account = "" } = useParams<{ account: string }>();
  const accounts = useAtomValue(accountsAtom);
  const shared = useAtomValue(sharedGalaxiesAtom);
  const sharedLoaded = useAtomValue(sharedGalaxiesLoadedAtom);
  const refreshShared = useSetAtom(refreshSharedGalaxiesAtom);

  useEffect(() => {
    if (!sharedLoaded) void refreshShared();
  }, [sharedLoaded, refreshShared]);

  if (!account) return false;
  // The shared-claim list is AUTHORITATIVE for "guest": a galaxy shared with the
  // viewer is a read-only claim, never a membership. Check it FIRST so that even
  // if the galaxy leaked into accountsAtom on some path, the guest flag — and the
  // write-affordance hiding it drives — still holds (a write button that only
  // 403s is worse than a hidden one). A real member galaxy is never in this list.
  if (shared.some((g) => g.account_login === account)) return true;
  // Otherwise a member (own / connected galaxy) is never a guest.
  if (accounts.includes(account)) return false;
  return false;
}
